import { DateTime } from 'luxon';
import config from '~config';
import type { TagNormalized } from '~redux/modules/tags.types';
import type { GetTimesheetProposalApiResponse } from '~redux/modules/timely/api-slice/GENERATED_ARTIFACT';
import type { EnrichedProject } from '~types/project';
import { calculateDuration } from '~utils/durations';
import type { LinkedWorkOption } from '../hooks/useLinkedWorkOptions';
import type {
  ProposalActivity,
  TimesheetEntry,
  TimesheetSummaryBreakdownItem,
} from '../types/types.TimesheetProposals';
import { partitionItemsByContainment } from './util.itemContainment';
import { getEditedEntryIdsFromLogs } from './util.logHelpers';

const sortByStartedAt = <T extends { started_at?: string | null }>(items: T[]): T[] =>
  [...items].sort(
    (a, b) => new Date(a.started_at || 0).getTime() - new Date(b.started_at || 0).getTime()
  );

/**
 * Transform a timesheet proposal into the shape the UI consumes:
 *   - `assignedEntries` — one entry per project-assigned DB row.
 *   - `unassignedItems` — items contained by no entry (derived via timestamp
 *     containment over the flat `proposal.items[]` list).
 */
export const transformProposalToTimesheetEntries = (
  proposal?: GetTimesheetProposalApiResponse['proposal']
): { assignedEntries: TimesheetEntry[]; unassignedItems: ProposalActivity[] } => {
  if (!proposal) {
    return { assignedEntries: [], unassignedItems: [] };
  }

  // The Rails artifact types `proposal.log` as opaque `object[]`; the structured
  // log contract (action / details / user_id) lives on `getEditedEntryIdsFromLogs`.
  // Bridge the two at this read boundary — the runtime shape matches.
  const editedEntryIds = getEditedEntryIdsFromLogs(
    proposal.log as Parameters<typeof getEditedEntryIdsFromLogs>[0],
    proposal.entries ?? []
  );
  const realEntries = proposal.entries ?? [];
  // Rails serves item ids (and the entry FK) as NUMBERS; this domain is
  // string-keyed, so coerce both to string at the boundary. Containment is by
  // timestamp (not by the FK), so this is purely an id-width normalization.
  const allItems: ProposalActivity[] = (proposal.items ?? []).map(item => ({
    ...item,
    id: String(item.id),
    timesheet_proposal_entry_id:
      item.timesheet_proposal_entry_id == null
        ? item.timesheet_proposal_entry_id
        : String(item.timesheet_proposal_entry_id),
  }));

  const { byEntryId: itemsByEntryId, unassigned } = partitionItemsByContainment(
    allItems,
    realEntries
  );

  const assignedEntries: TimesheetEntry[] = realEntries.map(entry => {
    const sortedActivities = sortByStartedAt(itemsByEntryId.get(entry.id) ?? []);
    // An entry's authored range is the source of truth for its duration. Items
    // live inside it but don't drive its geometry — drag-created empty entries
    // still have a real authored range.
    // Timed entries: derive duration from the authored range. Untimed entries
    // (legacy empties): fall back to the stored duration_minutes, which the DB
    // requires whenever a range is absent.
    const entryDurationSeconds =
      entry.started_at && entry.ended_at
        ? Math.max(
            0,
            (new Date(entry.ended_at).getTime() - new Date(entry.started_at).getTime()) / 1000
          )
        : (entry.duration_minutes ?? 0) * 60;
    return {
      // Rails serves entry ids as NUMBERS; this domain is string-keyed
      // end-to-end. Coerce at the boundary (matches the hasRegenerate lookup
      // below and the string-keyed Redux/Pusher/callback surface).
      id: String(entry.id),
      day: entry.date,
      // timely_project_id is NOT NULL at the DB level; the Zod schema still
      // types it as nullable. Coerce a defensive 0 in the impossible null case
      // to match the client-side "pending" sentinel.
      projectId: entry.timely_project_id ?? 0,
      duration: calculateDuration(entryDurationSeconds),
      description: entry.note || '',
      labelIds: entry.timely_label_ids || [],
      linkedResourceId: entry.resource_id || null,
      activities: sortedActivities,
      // editedEntryIds holds string-coerced keys; normalize the (numeric on
      // Rails, UUID on TIC) entry id the same way before the lookup.
      hasRegenerate: editedEntryIds.has(String(entry.id)),
      started_at: entry.started_at ?? null,
      ended_at: entry.ended_at ?? null,
    };
  });

  // Sort entries chronologically by their authored start. Entries with no
  // range fall to the end (legacy empty entries; should be rare).
  const sortedAssignedEntries = assignedEntries.sort((a, b) => {
    if (!a.started_at && !b.started_at) return 0;
    if (!a.started_at) return 1;
    if (!b.started_at) return -1;
    return new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
  });

  return {
    assignedEntries: sortedAssignedEntries,
    unassignedItems: sortByStartedAt(unassigned),
  };
};

/** A pair of ISO timestamps. The shape used everywhere ranges flow through Redux. */
export type TimeRange = { started_at: string; ended_at: string };

/**
 * Parse an ISO timestamp to epoch ms. Shared by every range/overlap/adjacency
 * primitive in this module so mixed ISO formats (UTC `Z` vs offset `+02:00`)
 * compare correctly across day-local slots, server entries, and activities.
 */
export const toMs = (iso: string): number => DateTime.fromISO(iso).toMillis();

/** Project any object carrying optional started_at/ended_at into a TimeRange[], dropping entries with null/undefined bounds. */
export const toRanges = <T extends { started_at?: string | null; ended_at?: string | null }>(
  items: T[]
): TimeRange[] =>
  items.flatMap(i =>
    i.started_at != null && i.ended_at != null
      ? [{ started_at: i.started_at, ended_at: i.ended_at }]
      : []
  );

/** Type guard: narrow an object with optional started_at/ended_at to one that has both. */
export const isRangedActivity = <
  T extends { started_at?: string | null; ended_at?: string | null },
>(
  a: T
): a is T & TimeRange => a.started_at != null && a.ended_at != null;

/**
 * Equality by instant on `started_at` + `ended_at`. Epoch-based (like
 * `rangesOverlap` / `mergeContiguousRanges`) so the same moment in mixed ISO
 * formats (`.000Z` vs `+00:00`) compares equal. Callers with nullable bounds
 * must narrow via `isRangedActivity` first.
 */
export const rangesEqual = (a: TimeRange, b: TimeRange): boolean =>
  DateTime.fromISO(a.started_at).toMillis() === DateTime.fromISO(b.started_at).toMillis() &&
  DateTime.fromISO(a.ended_at).toMillis() === DateTime.fromISO(b.ended_at).toMillis();

/**
 * Strict overlap. Adjacent ranges (one ends exactly when the next starts) are
 * not overlapping. Epoch-based so it works across mixed ISO formats (`.000Z`
 * vs `+00:00`) coming from different sources.
 */
export const rangesOverlap = (a: TimeRange, b: TimeRange): boolean => {
  const aStart = DateTime.fromISO(a.started_at).toMillis();
  const aEnd = DateTime.fromISO(a.ended_at).toMillis();
  const bStart = DateTime.fromISO(b.started_at).toMillis();
  const bEnd = DateTime.fromISO(b.ended_at).toMillis();
  return aStart < bEnd && bStart < aEnd;
};

/**
 * Sort, then fold adjacent or overlapping ranges into single ranges. Epoch-based
 * compare so mixed ISO formats (slots in local offset, activities in UTC) sort
 * and merge consistently.
 */
export const mergeContiguousRanges = (ranges: TimeRange[]): TimeRange[] => {
  if (ranges.length === 0) return [];

  const withMs = ranges.map(r => ({
    started_at: r.started_at,
    ended_at: r.ended_at,
    startMs: DateTime.fromISO(r.started_at).toMillis(),
    endMs: DateTime.fromISO(r.ended_at).toMillis(),
  }));
  withMs.sort((a, b) => a.startMs - b.startMs);

  // Build the clean TimeRange[] directly; track the running end in a local so we
  // never carry the millis onto the result or map over it at the end.
  const merged: TimeRange[] = [{ started_at: withMs[0].started_at, ended_at: withMs[0].ended_at }];
  let lastEndMs = withMs[0].endMs;
  for (let i = 1; i < withMs.length; i++) {
    const next = withMs[i];
    if (lastEndMs >= next.startMs) {
      if (next.endMs > lastEndMs) {
        merged[merged.length - 1].ended_at = next.ended_at;
        lastEndMs = next.endMs;
      }
    } else {
      merged.push({ started_at: next.started_at, ended_at: next.ended_at });
      lastEndMs = next.endMs;
    }
  }
  return merged;
};

/**
 * Slots covering one local-time day at the given interval. Length follows the
 * timeline gutter's local-day window, so DST days yield 23/24/25h of slots.
 */
export const generateIntervalSlots = (date: string, intervalMinutes: number): TimeRange[] => {
  if (intervalMinutes <= 0) return [];

  const dayStart = DateTime.fromISO(date).startOf('day');
  const dayEnd = dayStart.plus({ days: 1 });

  const slots: TimeRange[] = [];
  let cursor = dayStart;
  while (cursor < dayEnd) {
    const next = cursor.plus({ minutes: intervalMinutes });
    const startedAt = cursor.toISO();
    const endedAt = next.toISO();
    if (startedAt && endedAt) {
      slots.push({ started_at: startedAt, ended_at: endedAt });
    }
    cursor = next;
  }
  return slots;
};

/**
 * Helper to normalize color to #RRGGBB format
 */
const normalizeColor = (color: string | null | undefined, defaultColor: string): string => {
  const rawColor = color || defaultColor;
  return `#${rawColor.replace('#', '').substring(0, 6)}`;
};

/**
 * Helper to sort and append uncategorized item to a list
 */
const finalizeCategorizedItems = (
  items: TimesheetSummaryBreakdownItem[],
  uncategorizedSeconds: number,
  uncategorizedName: string,
  uncategorizedIconUrl?: string
): TimesheetSummaryBreakdownItem[] => {
  const sorted = items.filter(item => item.seconds > 0).sort((a, b) => b.seconds - a.seconds);

  if (uncategorizedSeconds <= 0) return sorted;

  return [
    ...sorted,
    {
      name: uncategorizedName,
      seconds: uncategorizedSeconds,
      iconUrl: uncategorizedIconUrl,
      isUncategorized: true,
    },
  ];
};

const sumItemSeconds = (items: ProposalActivity[]): number =>
  items.reduce((total, item) => {
    if (!item.started_at || !item.ended_at) return total;
    return total + (new Date(item.ended_at).getTime() - new Date(item.started_at).getTime()) / 1000;
  }, 0);

/**
 * Aggregate project breakdown from assigned entries, optionally folding in
 * unassigned items as an "Unassigned" bucket (items with no owning entry).
 */
export const aggregateProjects = (
  entries: TimesheetEntry[],
  projects: EnrichedProject[],
  unassignedItems: ProposalActivity[] = []
): { projectItems: TimesheetSummaryBreakdownItem[]; totalDurationSeconds: number } => {
  const projectColorMap = new Map<number, string | null | undefined>(
    projects.map(p => [p.base.id, p.base.color || p.base.client.color])
  );

  const projectSecondsMap = new Map<
    number,
    { seconds: number; name: string; subHeading?: string; color: string }
  >();
  let assignedSeconds = 0;

  entries.forEach(entry => {
    const seconds = entry.duration.total_seconds;
    assignedSeconds += seconds;

    const existing = projectSecondsMap.get(entry.projectId);
    if (existing) {
      existing.seconds += seconds;
      return;
    }

    const projectFromRedux = projects.find(p => p.base.id === entry.projectId);
    projectSecondsMap.set(entry.projectId, {
      name: projectFromRedux?.base.name ?? 'Unknown Project',
      subHeading: projectFromRedux?.base.client.name,
      seconds,
      color: normalizeColor(projectColorMap.get(entry.projectId), '0055ff'),
    });
  });

  const assignedItems: TimesheetSummaryBreakdownItem[] = Array.from(projectSecondsMap.values())
    .filter(item => item.seconds > 0)
    .map(item => ({
      name: item.name,
      seconds: item.seconds,
      subHeading: item.subHeading,
      color: item.color,
      isUncategorized: false,
    }))
    .sort((a, b) => b.seconds - a.seconds);

  const unassignedSeconds = sumItemSeconds(unassignedItems);
  const projectItems =
    unassignedSeconds > 0
      ? [
          ...assignedItems,
          {
            name: 'Unassigned',
            seconds: unassignedSeconds,
            color: normalizeColor(null, '999999'),
            isUncategorized: true,
          },
        ]
      : assignedItems;

  return {
    projectItems,
    totalDurationSeconds: assignedSeconds,
  };
};

/**
 * Aggregate a tag breakdown from entries. Pure, so it composes inside selectors.
 */
export const aggregateTags = (
  entries: TimesheetEntry[],
  tagEntities: Record<number, TagNormalized>
): TimesheetSummaryBreakdownItem[] => {
  const tagTimes = new Map<number, number>();
  let untaggedSeconds = 0;

  entries.forEach(entry => {
    const seconds = entry.duration.total_seconds;

    if (!entry.labelIds?.length) {
      untaggedSeconds += seconds;
    } else {
      entry.labelIds.forEach(tagId => {
        const tag = tagEntities[tagId];
        if (tag && !tag.children?.length) {
          tagTimes.set(tagId, (tagTimes.get(tagId) || 0) + seconds);
        }
      });
    }
  });

  const tagItemsRaw = Array.from(tagTimes.entries()).map(([tagId, seconds]) => ({
    name: tagEntities[tagId]?.name || `Tag ${tagId}`,
    seconds,
    iconUrl: tagEntities[tagId]?.emoji,
    isUncategorized: false,
  }));

  const defaultTagEmojiUrl = `${config.emojiCdnPath}/${config.defaultTagEmoji}`;
  return finalizeCategorizedItems(tagItemsRaw, untaggedSeconds, 'Untagged', defaultTagEmojiUrl);
};

/** Shown for a linked resource no lookup has resolved and none still in flight will. */
export const UNRESOLVED_LINKED_WORK_LABEL = 'Linked item';

/**
 * Aggregate a linked-work breakdown from entries. Pure, so it composes inside selectors.
 *
 * @param isLookupPending - Whether a lookup that could still resolve a linked resource missing from
 *   `taskOptions` is in flight. While one is, the row says "Loading..."; once none is, the resource
 *   is not coming (its fetch failed, or it is gone or inaccessible) and the row settles on
 *   {@link UNRESOLVED_LINKED_WORK_LABEL} rather than loading forever.
 */
export const aggregateLinkedWork = (
  entries: TimesheetEntry[],
  taskOptions: LinkedWorkOption[],
  isLookupPending: boolean
): TimesheetSummaryBreakdownItem[] => {
  const linkedWorkTimes = new Map<
    string,
    { title: string; iconUrl?: string; iconToolId?: string; seconds: number }
  >();
  let unlinkedSeconds = 0;

  const taskLookup = new Map(
    taskOptions.map(task => [
      task.id,
      { title: task.label, iconUrl: task.toolIconUrl, iconToolId: task.toolId },
    ])
  );

  entries.forEach(entry => {
    const seconds = entry.duration.total_seconds;

    if (!entry.linkedResourceId) {
      unlinkedSeconds += seconds;
    } else {
      const taskInfo = taskLookup.get(entry.linkedResourceId);
      const title =
        taskInfo?.title || (isLookupPending ? 'Loading...' : UNRESOLVED_LINKED_WORK_LABEL);
      const iconUrl = taskInfo?.iconUrl;
      const iconToolId = taskInfo?.iconToolId;

      const existing = linkedWorkTimes.get(entry.linkedResourceId);
      if (existing) {
        existing.seconds += seconds;
      } else {
        linkedWorkTimes.set(entry.linkedResourceId, { title, iconUrl, iconToolId, seconds });
      }
    }
  });

  const linkedWorkItemsRaw = Array.from(linkedWorkTimes.values()).map(
    ({ title, iconUrl, iconToolId, seconds }) => ({
      name: title,
      seconds,
      iconUrl,
      iconToolId,
      isUncategorized: false,
    })
  );

  return finalizeCategorizedItems(linkedWorkItemsRaw, unlinkedSeconds, 'Unlinked work');
};
